import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { ImageField } from "@/admin/Field";

const inputCls = "w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0F2C59]";

function Row({ label, children, help }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      {children}
      {help && <p className="text-xs text-slate-400 mt-1">{help}</p>}
    </div>
  );
}

export default function SiteContent() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS({ brand: {}, homepage: {}, founder: {}, ...r.data }));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", s); toast.success("Saved — changes are live on the site."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="h-64 bg-slate-100 animate-pulse" />;
  const b = s.brand || {}, hp = s.homepage || {}, f = s.founder || {};
  const setB = (k, v) => setS({ ...s, brand: { ...b, [k]: v } });
  const setHP = (k, v) => setS({ ...s, homepage: { ...hp, [k]: v } });
  const setF = (k, v) => setS({ ...s, founder: { ...f, [k]: v } });

  return (
    <div className="max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif-display text-2xl font-bold text-[#111827]">Site Content</h1>
        <button data-testid="sitecontent-save" onClick={save} disabled={saving} className="bg-[#0F2C59] text-white px-6 py-2.5 text-sm font-semibold disabled:opacity-50">
          {saving ? "Saving…" : "Save & Publish"}
        </button>
      </div>

      <div className="border border-slate-200 bg-white p-6 mb-6">
        <h2 className="font-semibold text-slate-700 mb-4">Brand / Logo</h2>
        <Row label="Header logo (leave empty to use the default monogram wordmark)" help="A wide/horizontal transparent PNG works best in the header.">
          <ImageField value={b.logo_url} onChange={(v) => setB("logo_url", v)} testid="brand-logo" />
        </Row>
      </div>

      <div className="border border-slate-200 bg-white p-6 mb-6">
        <h2 className="font-semibold text-slate-700 mb-4">Homepage Hero</h2>
        <Row label="Eyebrow"><input className={inputCls} value={hp.hero_eyebrow || ""} onChange={(e) => setHP("hero_eyebrow", e.target.value)} placeholder="Impactful Mindset 101 · Publishing & Ideas Platform" /></Row>
        <div className="grid grid-cols-3 gap-3">
          <Row label="Headline line 1"><input className={inputCls} value={hp.hero_line1 || ""} onChange={(e) => setHP("hero_line1", e.target.value)} placeholder="Better Thinking." /></Row>
          <Row label="Headline line 2"><input className={inputCls} value={hp.hero_line2 || ""} onChange={(e) => setHP("hero_line2", e.target.value)} placeholder="Better Decisions." /></Row>
          <Row label="Headline line 3 (blue)"><input className={inputCls} value={hp.hero_line3 || ""} onChange={(e) => setHP("hero_line3", e.target.value)} placeholder="A More Intentional Life." /></Row>
        </div>
        <Row label="Supporting paragraph"><textarea rows={3} className={inputCls} value={hp.hero_sub || ""} onChange={(e) => setHP("hero_sub", e.target.value)} placeholder="Practical, psychologically credible ideas…" /></Row>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Primary button text"><input className={inputCls} value={hp.cta_primary_text || ""} onChange={(e) => setHP("cta_primary_text", e.target.value)} placeholder="Start Here" /></Row>
          <Row label="Primary button link"><input className={inputCls} value={hp.cta_primary_link || ""} onChange={(e) => setHP("cta_primary_link", e.target.value)} placeholder="/start-here" /></Row>
          <Row label="Secondary button text"><input className={inputCls} value={hp.cta_secondary_text || ""} onChange={(e) => setHP("cta_secondary_text", e.target.value)} placeholder="Explore Ideas" /></Row>
          <Row label="Secondary button link"><input className={inputCls} value={hp.cta_secondary_link || ""} onChange={(e) => setHP("cta_secondary_link", e.target.value)} placeholder="/ideas" /></Row>
        </div>
      </div>

      <div className="border border-slate-200 bg-white p-6">
        <h2 className="font-semibold text-slate-700 mb-4">Founder</h2>
        <Row label="Founder photograph"><ImageField value={f.photo_url} onChange={(v) => setF("photo_url", v)} testid="founder-photo" /></Row>
        <Row label="Name"><input className={inputCls} value={f.name || ""} onChange={(e) => setF("name", e.target.value)} placeholder="Lance Cervantes" /></Row>
        <Row label="Descriptor"><input className={inputCls} value={f.descriptor || ""} onChange={(e) => setF("descriptor", e.target.value)} placeholder="Author · Educator · Entrepreneur" /></Row>
        <Row label="Short biography (homepage)"><textarea rows={4} className={inputCls} value={f.short_bio || ""} onChange={(e) => setF("short_bio", e.target.value)} /></Row>
        <Row label="Quotation"><input className={inputCls} value={f.quote || ""} onChange={(e) => setF("quote", e.target.value)} placeholder="— Lance Cervantes" /></Row>
      </div>
    </div>
  );
}
